// Vendor imports
const Promise = require('bluebird')
const _ = require('lodash')
const Popper = require('popper.js').default
// Shared imports
const cssUtils = require('shared/css-utils')
const nameUtils = require('shared/name-utils')
const sharedUtils = require('shared/utils')
// Client lib imports
const { send: ajax } = require('client/lib/request')
const { track } = require('client/lib/analytics')
const airbrake = require('client/lib/airbrake')

require('client/lib/jquery/register-plugin')

const BLOCK_CLASSNAME = 'SuperAutocomplete'
const ITEM_CLASSNAME = BLOCK_CLASSNAME + '__item'
const ITEM_SELECTOR = '.' + ITEM_CLASSNAME
const ITEM_ACTIVE_CLASSNAME = ITEM_CLASSNAME + '--active'
const ITEM_ACTIVE_SELECTOR = '.' + ITEM_ACTIVE_CLASSNAME
const ITEM_NAME_CLASSNAME = ITEM_CLASSNAME + '-name'
const ITEM_PHOTO_CLASSNAME = ITEM_CLASSNAME + '-photo'
const ITEM_USERNAME_CLASSNAME = ITEM_CLASSNAME + '-username'

class SuperAutocomplete {
  static defaultOptions(options) {
    return _.defaults(options, {
      supersearchResults: [],
      // Default to backwards-compatibility with client-v1 app
      onNavigate: href => app.router.navigate(href),
      onSearch: text => app.trigger('search', text)
    })
  }

  constructor(el, options) {
    this.options = SuperAutocomplete.defaultOptions(options)
    this.resultFilter = options.resultFilter || (() => true)
    this.el = el
    this.$el = $(el)
    this.$el.on('keydown', this.onKeyDown.bind(this))
    this.$el.on('input', this.onInputChange.bind(this))
    this.hide = this.hide.bind(this)
    $(window).on('resize', this.hide)
    $(document).on('click', this.hide)
    if (typeof app === 'object' && typeof app.on === 'function') {
      app.on('navigationend', this.hide)
    }
  }

  update(options) {
    this.options = SuperAutocomplete.defaultOptions(options)
  }

  destroy() {
    $(window).off('resize', this.hide)
    $(document).off('click', this.hide)
    if (typeof app === 'object' && typeof app.off === 'function') {
      app.off('navigationend', this.hide)
    }
    if (this.$collection) {
      this.$collection.remove()
      delete this.$collection
    }
    if (this._popper) {
      this._popper.destroy()
      delete this._popper
    }
  }

  setupCollection() {
    if (this.$collection) {
      return
    }
    this.$collection = $('<ul/>')
    .addClass(BLOCK_CLASSNAME)
    .on('click', ITEM_SELECTOR, this.onItemClick.bind(this))
    .insertAfter(this.$el)
    this._popper = new Popper(
      this.$el[0],
      this.$collection[0],
      {
        placement: 'bottom'
      }
    )
  }

  hide(event) {
    if (event && event.isImmediatePropagationStopped()) {
      return
    }
    if (this.$collection && this.$collection.length) {
      this.$collection.empty()
      this.$collection.removeClass(ITEM_ACTIVE_CLASSNAME)
    }
  }

  onArrowDownPress(event) {
    event.preventDefault()
    let $selected = this.$collection.find(ITEM_ACTIVE_SELECTOR)
    if (this.$el.val()) {
      event.stopPropagation()
    }
    if (!$selected.length) {
      $selected = this.$collection.find(ITEM_SELECTOR).first()
      if ($selected.length) {
        $selected.addClass(ITEM_ACTIVE_CLASSNAME)
      }
      return
    }
    const $next = $selected.next()
    if ($next.length) {
      $selected.removeClass(ITEM_ACTIVE_CLASSNAME)
      $next.addClass(ITEM_ACTIVE_CLASSNAME)
    }
  }

  onArrowUpPress(event) {
    event.preventDefault()
    if (this.$el.val()) {
      event.stopPropagation()
    }
    let $selected = this.$collection.find(ITEM_ACTIVE_SELECTOR)
    if (!$selected.length) {
      return
    }
    const $prev = $selected.prev()
    if ($prev.length) {
      $selected.removeClass(ITEM_ACTIVE_CLASSNAME)
      $prev.addClass(ITEM_ACTIVE_CLASSNAME)
    }
  }

  onItemClick(event) {
    $(event.currentTarget).addClass(ITEM_ACTIVE_CLASSNAME)
    this.onItemSelected(event)
  }

  onItemSelected(event) {
    const val = this.$el.val()
    const $active = this.$collection.find(ITEM_ACTIVE_SELECTOR)
    if (!val || !$active.length) {
      this.$el.focus()
      this.hide()
      return
    }
    event.preventDefault()
    const complete = $active.attr('data-complete')
    const type = $active.attr('data-type')
    track('typeahead-select', {
      type,
      'text': complete,
      'search': val,
      metabase: {
        'actioned_object_type': 'search',
        'actioned_object_id': type,
        'extra_properties': val.length
      }
    })

    this.$el.val(complete).trigger('input')
    this.$el.blur()
    this.hide()

    switch (type) {
      case 'contact':
        this.options.onNavigate('/' + complete)
        break
      case 'search':
      case 'google':
        this.options.onSearch(complete)
        break
      case 'url':
        this.options.onNavigate('/!' + $active.attr('data-meta'))
        break
    }
  }

  onKeyDown(event) {
    this.setupCollection()
    switch (event.keyCode) {
      case 38: // up
        this.onArrowUpPress(event)
        break
      case 40: // down
        this.onArrowDownPress(event)
        break
      case 13: // enter
      case 9: // tab
        this.onItemSelected(event)
        break
      case 27: // escape
        this.hide()
        break
    }
  }

  onInputChange() {
    this.setupCollection()
    const val = this.$el.val()
    if (!val) {
      this.$collection.empty()
      return
    }
    const matches = matcher(val, this.options.supersearchResults)
    Promise.all([
      getGoogle(val),
      getUsers(val)
    ])
    .spread((suggestions, users) => {
      if (!this.$collection) {
        return // Results came in after .destroy() call
      }
      this.$collection.empty()
      // Gather top results, stripping out dupes and grouping by type
      const n = matches.length > 5 ? 3 : 10 - matches.length
      const topSuggestions = suggestions.slice(0, Math.max(3, n))
      const groupedResults = _.chain([
        matches,
        users,
        topSuggestions
      ])
      .flatten()
      .uniqBy('complete')
      .groupBy('type')
      .value()
      // Show results grouped by type in a flat list
      _.chain([
        groupedResults.contact,
        groupedResults.search,
        groupedResults.google
      ])
      .flatten()
      .compact()
      .filter(this.resultFilter)
      .value()
      .forEach(option => {
        let itemHtml = `
          <span class="${ITEM_NAME_CLASSNAME}">${option.names[0]}</span>
        `
        if (option.type === 'contact') {
          itemHtml = `
            <span
              class="${ITEM_PHOTO_CLASSNAME}"
              style="${cssUtils.backgroundImage(option.photo)}">
            </span>
            ${itemHtml}
            <em class="${ITEM_USERNAME_CLASSNAME}">${option.names[1]}</em>
          `
        }
        $('<li>')
        .addClass(ITEM_CLASSNAME)
        .attr({
          'data-complete': option.complete,
          'data-meta': option.meta || '',
          'data-type': option.type
        })
        .html(itemHtml)
        .appendTo(this.$collection)
      })
    })
    .catch(err => airbrake.notify(err))
  }
}

function matcher(input, matchObjects, matchKey = 'names') {
  const re = sharedUtils.fuzzyMatcherRegexp(input)
  return (matchObjects || [])
  .filter(obj => obj[matchKey].some(name => re.test(name)))
  .slice(0, 10)
}

let mostRecentGooglePromise = null
let mostRecentGoogleQuery = ''
let mostRecentGoogleResults = []
function getGoogle(query) {
  query = query.trim()
  if (query === mostRecentGoogleQuery) {
    mostRecentGooglePromise = null
    return Promise.resolve(mostRecentGoogleResults)
  }
  if (_.isEmpty(query)) {
    mostRecentGooglePromise = null
    mostRecentGoogleQuery = ''
    return Promise.resolve(mostRecentGoogleResults = [])
  }
  // JSONP calls cannot be aborted.
  const thisGooglePromise = ajax({
    url: 'https://suggestqueries.google.com/complete/search',
    method: 'get',
    dataType: 'jsonp',
    data: {
      q: query,
      client: 'chrome'
    }
  })
  return (mostRecentGooglePromise = thisGooglePromise)
  .then(response => {
    if (thisGooglePromise !== mostRecentGooglePromise) {
      return mostRecentGoogleResults
    }

    const cleanQuery = nameUtils.displayText(query)
    const results = [{
      names: [cleanQuery],
      type: 'google',
      complete: cleanQuery
    }]
    const suggestions = response[1]
    if (suggestions) {
      suggestions
      .filter(suggestion => !sharedUtils.isExternalUrl(suggestion))
      .slice(0, 6)
      .forEach(suggestion => {
        const cleanSuggestion = nameUtils.displayText(suggestion)
        if (cleanSuggestion !== cleanQuery) {
          results.push({
            names: [cleanSuggestion],
            type: 'google',
            complete: cleanSuggestion
          })
        }
      })
    }
    mostRecentGoogleQuery = query
    return mostRecentGoogleResults = results
  })
}

let mostRecentUsersPromise = null
function getUsers(text) {
  text = text.trim()
  if (mostRecentUsersPromise) {
    mostRecentUsersPromise.req.abort()
    mostRecentUsersPromise = null
  }
  if (_.isEmpty(text)) {
    return Promise.resolve([])
  }
  const thisUsersPromise = ajax({
    url: '/api/v0.1/users/search',
    method: 'post',
    data: { text }
  })
  return (mostRecentUsersPromise = thisUsersPromise)
  .then(response => {
    if (_.isEmpty(response.items)) {
      return []
    }
    return response.items
    .slice(0, 3)
    .map(user => ({
      names: [user['display_name'], '@' + user.username],
      type: 'contact',
      photo: user['photo_url'],
      complete: user.username
    }))
  })
  .catch(error => {
    const statusText = _.get(error, 'xhr.statusText')
    if (statusText === 'abort') {
      return []
    }
    throw error
  })
  .finally(() => {
    if (thisUsersPromise === mostRecentUsersPromise) {
      mostRecentUsersPromise = null
    }
  })
}

$.registerPlugin(SuperAutocomplete, 'supercomplete')

module.exports = SuperAutocomplete
